import config from 'config'
import { StorefrontModule } from '@vue-storefront/core/lib/modules'
import { isServer } from '@vue-storefront/core/helpers'
import * as cartMutationTypes from '@vue-storefront/core/modules/cart/store/mutation-types'
import { module } from './store'
import { creating } from './creating'
import { isCheckoutPath, readVariant, resolveRoute } from './helpers'
import { FREE_CHECKOUT_ROUTE, FULLY_COVERED_GETTER } from 'src/modules/gift-card/constants'

export const KEY = 'ab-test'

export const AbTestModule: StorefrontModule = function ({ app, store, router }) {
  store.registerModule(KEY, module)

  if (isServer) {
    return
  }

  // Seed the store from a held cookie so a returning visitor's first render is
  // already correct, rather than waiting on the cart-token round-trip. It must
  // run after mount: registration precedes app.$mount(), so committing earlier
  // would disagree with the cached SSR HTML and break hydration.
  app.$once('hook:mounted', () => {
    store.dispatch(`${KEY}/syncVariant`)
  })

  const sync = () => {
    const cartToken = store.getters['cart/getCartToken']

    if (!cartToken) {
      return Promise.resolve(null)
    }

    return store.dispatch(`${KEY}/syncVariant`, { cartToken, force: true })
  }

  // The checkout path this variant is allowed to use, or null when the current
  // path is already the right one.
  const correctedPath = (path: string, variant: string | null, routes: Record<string, string>): string | null => {
    const assignedRoute = resolveRoute(variant, routes)
    const segments = String(path || '').split('/').filter(Boolean)

    if (segments[segments.length - 1] === assignedRoute) {
      return null
    }

    return `/${[...segments.slice(0, -1), assignedRoute].join('/')}`
  }

  // The cart token is the quote we stamp, so assignment rides on its commit.
  // Forced when the token is being created (a fresh quote is unstamped, and
  // 'cart/connect' raises creating.count) or when it arrives while a checkout
  // is already open, which the router hook can miss on an initial navigation.
  // endsWith, not ===: the constant already carries the module prefix and Vuex
  // prepends the namespace again, making the real type 'cart/cart/SRV_TOKEN'.
  store.subscribe(({ type, payload }) => {
    if (type.endsWith(cartMutationTypes.CART_LOAD_CART_SERVER_TOKEN) && payload) {
      const onCheckout = isCheckoutPath(router.currentRoute?.path || '', config.abTest?.checkout || {})

      store.dispatch(`${KEY}/syncVariant`, { cartToken: payload, force: creating.count > 0 || onCheckout })
    }
  })

  // Second chance: a quote the first attempt could not stamp gets stamped on
  // the way into a checkout, which is the last moment before an order exists.
  // Assignment can land after the navigation, so the route is re-checked once
  // it resolves - otherwise a first visit straight to a checkout URL pays on
  // one processor while the quote carries the other.
  const isFreeGiftCardCheckout = (route): boolean => route?.name === FREE_CHECKOUT_ROUTE &&
    Boolean(store.getters[FULLY_COVERED_GETTER])

  router.afterEach(to => {
    const routes = config.abTest?.checkout || {}

    if (!isCheckoutPath(to.path, routes)) {
      return
    }

    sync().then(variant => {
      const current = router.currentRoute?.path || ''

      if (!variant || !isCheckoutPath(current, routes) || isFreeGiftCardCheckout(router.currentRoute)) {
        return
      }

      const target = correctedPath(current, variant, routes)

      if (target) {
        router.replace({ path: target, query: router.currentRoute.query })
      }
    })
  })

  // Stops a customer from typing /checkout or /kustom-checkout directly and
  // picking whichever processor they like, which would decouple the order's
  // recorded variant from the processor that actually took the money.
  router.beforeEach((to, from, next) => {
    const routes = config.abTest?.checkout || {}

    if (!isCheckoutPath(to.path, routes)) {
      next()
      return
    }

    if (isFreeGiftCardCheckout(to)) {
      next()
      return
    }

    const variant = readVariant()

    // Unknown variant (no cookie yet, or assignment still in flight): never
    // block navigation on something we can't yet resolve. On the server
    // readVariant() is always null, so this branch is also what keeps SSR
    // from ever redirecting - only the client, after hydration, can enforce
    // the assignment.
    if (variant === null) {
      next()
      return
    }

    const target = correctedPath(to.path, variant, routes)

    if (!target) {
      next()
      return
    }

    // Known trade-off, accepted deliberately: an abandoned-cart email link
    // carries the variant the quote was stamped with. If the recipient's
    // cookie was cleared and they were reassigned since, this redirect sends
    // them to their new variant while the quote keeps its original stamp, so
    // quote and order disagree for that order.
    next({ path: target, query: to.query })
  })
}
